import React from 'react'
import { TouchableOpacity, View } from 'react-native'
import { useTheme } from '@ui-kitten/components'
import { Maybe, Pill as PillType, Product } from '@src/types.ts'
import { Pill, Radio, Text } from '@components/base'
import { StyleService, useStyleSheet } from '@style/service.ts'
import { ThemeColor } from '@style/theme'
import { ProgramDetails } from './ProgramDetails'

interface ProgramCardProps {
  product: Pick<Product, 'title' | 'description' | 'details' | 'pill' | 'metadata'>
  onSelect: () => void
  selected: boolean
  price: React.ReactNode
  programDetailsTitle?: string
  useOptionBadgeForBestValue?: boolean
}

const BEST_VALUE_PILL_TEXT = 'best value'

const normalizePillText = (text?: Maybe<string>) => text?.trim().toLowerCase() ?? ''

const isBestValuePill = (pill: Maybe<PillType> | undefined) =>
  normalizePillText(pill?.text) === BEST_VALUE_PILL_TEXT

const getBorderColor = (selected: boolean, pill: Maybe<PillType> | undefined): ThemeColor => {
  if (pill?.fill) {
    return `theme.accent.${pill.fill}` as ThemeColor
  }
  if (selected) {
    return 'theme.success.base'
  }
  return 'theme.surface.base'
}

export const ProgramCard = ({
  product,
  onSelect,
  selected,
  price,
  programDetailsTitle,
  useOptionBadgeForBestValue = false,
}: ProgramCardProps) => {
  const styles = useStyleSheet(themedStyles)
  const theme = useTheme()
  const { title, description, details, pill, metadata } = product
  const shouldUseOptionBadge = useOptionBadgeForBestValue && isBestValuePill(pill)

  const borderColor = theme[getBorderColor(selected, pill)]

  return (
    <TouchableOpacity
      style={[styles.container, { borderColor }]}
      onPress={onSelect}
      accessibilityLabel={title}
      activeOpacity={1}
    >
      {pill &&
        (shouldUseOptionBadge ? (
          <View style={styles.optionBadge}>
            <Text type="regular" bold style={styles.optionBadgeText}>
              {pill.text}
            </Text>
          </View>
        ) : (
          <Pill
            text={pill.text}
            fill={pill.fill || undefined}
            status={(pill.type as any) || undefined}
            style={styles.tag}
          />
        ))}
      <View style={styles.header}>
        <View style={styles.row}>
          <Radio checked={selected} onPress={onSelect} />
          <View>
            <Text type="title-3">{metadata.short_title || title}</Text>
            {!!metadata.short_description && (
              <Text type="regular" style={styles.description}>
                {metadata.short_description}
              </Text>
            )}
          </View>
        </View>
        {price}
      </View>
      {!!description && (
        <Text type="regular" style={styles.description}>
          {description}
        </Text>
      )}
      {details && <ProgramDetails details={details} title={programDetailsTitle} />}
    </TouchableOpacity>
  )
}

const themedStyles = StyleService.create({
  container: {
    width: '100%',
    padding: 16,
    borderRadius: 16,
    borderWidth: 1,
    gap: 10,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  description: {
    color: 'theme.text.secondary',
  },
  row: {
    flexDirection: 'row',
    gap: 8,
  },
  tag: {
    zIndex: 1,
    top: -33,
    marginBottom: -33,
    alignSelf: 'center',
  },
  optionBadge: {
    position: 'absolute',
    top: -14,
    alignSelf: 'center',
    borderRadius: 8,
    backgroundColor: 'theme.marketing.yellow',
    paddingHorizontal: 12,
    paddingVertical: 4,
    zIndex: 1,
  },
  optionBadgeText: {
    color: 'theme.marketing.onText',
  },
})
